import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * A React hook for performing a query lazily. It does not execute on initial render but rather returns a `refetch` function that can be called manually with parameters to execute the query.
 * @template T
 * @param {function(any): Promise<{data: T}>} query - A function representing the query to be executed. This function should accept query parameters and return a Promise that resolves to the data.
 * @returns {{ data: T | null, loading: boolean, error: any, refetch: function(any): Promise<void> }} An object containing the following properties: `data`, `loading`, `error`, and `refetch`. `data` holds the query result, `loading` is a boolean indicating if the query is in progress, `error` contains any error that occurred during the query, and `refetch` is a function to execute the query manually.
 */
export const useLazyQuery = (query) => {
  const [data, setData] = useState(null);
  const [count, setCount] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const refetch = useCallback(
    async (...params) =>
      new Promise((resolve, reject) => {
        setLoading(true);
        setData(null);
        setError(null);
        query(...params)
          .then((response) => {
            setData(response.data);
            setCount(response.count);
            resolve(response);
          })
          .catch((err) => {
            setError(err);
            reject(err);
          })
          .finally(() => {
            setLoading(false);
          });
      }),
    [query],
  );

  return { data, loading, error, refetch, count };
};

/**
 * A React hook for performing a query immediately upon component mount. It automatically executes the provided query using the initial parameters.
 * @template T
 * @param {function(...any): Promise<{data: T}>} query - A function representing the query to be executed. This function should accept query parameters and return a Promise that resolves to the data.
 * @param {any[]} initialParams - An array containing the initial parameters to pass to the query function on the initial execution.
 * @returns {{ data: T | null, loading: boolean, error: any }} An object containing the following properties: data, loading, error. data holds the query result, loading is a boolean indicating if the query is in progress, and error contains any error that occurred during the query.
 */
export const useQuery = (query, ...initialParams) => {
  const initialParamsRef = useRef(initialParams);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setData(null);
    setError(null);

    try {
      const response = await query(...initialParamsRef.current);
      setData(response.data);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [query]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return { data, loading, error };
};

/**
 * A React hook for performing mutations. It returns a function that can be called with parameters to execute the mutation. This hook does not execute automatically but rather provides a function to manually trigger the mutation.
 * @template T
 * @param {function(...any): Promise<T>} query - A function representing the mutation to be performed. This function should accept mutation parameters and return a Promise that resolves to the result of the mutation.
 * @returns {[function(...any): Promise<void>, { data: T | null, loading: boolean, error: any }]} An array where the first element is the `customCallback` function to execute the mutation, and the second element is an object containing the following properties: `data`, `loading`, `error`. `data` holds the mutation result, `loading` is a boolean indicating if the mutation is in progress, and `error` contains any error that occurred during the mutation.
 */
export const useMutation = (query) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const customCallback = useCallback(
    (...args) =>
      new Promise((resolve, reject) => {
        setLoading(true);
        setError(null);
        setData(null);
        query(...args)
          .then((result) => {
            resolve(result);
            setData(result);
          })
          .catch((err) => {
            setError(err);
            reject(err);
          })
          .finally(() => {
            setLoading(false);
          });
      }),
    [query],
  );

  return [customCallback, { data, loading, error }];
};
